import { For, Show, createMemo, createSignal } from "solid-js";

import {
  useArchiveCustomField,
  useCreateCustomField,
  useCustomFields,
  useCustomFieldsForObject,
  useCustomObjects,
} from "~/lib/hooks";
import type {
  CustomFieldDefinitionCreate,
  CustomFieldEntityType,
  CustomFieldLookupTarget,
  CustomFieldType,
} from "~/lib/types";

type Props = {
  wsKey: () => string;
};

// Target = either a built-in entity_type or a custom_object_definition_id.
// Encoded in the dropdown as `entity:<kind>` or `object:<cod_id>` so the
// `<select>` `value` stays a single string.
type Target =
  | { kind: "entity"; entity_type: CustomFieldEntityType }
  | { kind: "object"; cod_id: string };

const ENTITY_TARGETS: CustomFieldEntityType[] = [
  "account",
  "contact",
  "deal",
  "product",
  "activity",
];

const ENTITY_LABEL: Record<CustomFieldEntityType, string> = {
  account: "Account",
  contact: "Contact",
  deal: "Deal",
  product: "Product",
  activity: "Activity",
};

const TYPE_LABEL: Record<CustomFieldType, string> = {
  text: "Text",
  number: "Number",
  date: "Date",
  select: "Select",
  multi_select: "Multi-select",
  lookup: "Lookup",
  email: "Email",
  url: "URL",
  phone: "Phone",
  rating: "Rating (1-5)",
  currency: "Currency",
};

const LOOKUP_TARGET_LABEL: Record<CustomFieldLookupTarget, string> = {
  account: "Account",
  contact: "Contact",
  user: "User",
  product: "Product",
  deal: "Deal",
  custom_record: "Custom record",
};

export function CustomFieldsPanel(props: Props) {
  const [target, setTarget] = createSignal<Target>({
    kind: "entity",
    entity_type: "account",
  });
  const customObjects = useCustomObjects(props.wsKey);

  const entityFields = useCustomFields(props.wsKey, () =>
    target().kind === "entity"
      ? (target() as Extract<Target, { kind: "entity" }>).entity_type
      : undefined,
  );
  const objectFields = useCustomFieldsForObject(props.wsKey, () =>
    target().kind === "object"
      ? (target() as Extract<Target, { kind: "object" }>).cod_id
      : undefined,
  );
  const activeFields = createMemo(() => {
    const t = target();
    const src = t.kind === "entity" ? entityFields.data : objectFields.data;
    return (src ?? []).filter((f) => !f.archived_at);
  });

  const createMutation = useCreateCustomField(props.wsKey);
  const archiveMutation = useArchiveCustomField(props.wsKey);

  const [showAdd, setShowAdd] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [addType, setAddType] = createSignal<CustomFieldType>("text");
  const [addLookupTarget, setAddLookupTarget] = createSignal<CustomFieldLookupTarget>("account");
  // Only used when addLookupTarget === 'custom_record' — picks which object.
  const [addLookupCodId, setAddLookupCodId] = createSignal<string>("");

  const targetLabel = () => {
    const t = target();
    if (t.kind === "entity") return t.entity_type;
    const obj = customObjects.data?.items.find((o) => o.id === t.cod_id);
    return obj?.label_singular.toLowerCase() ?? "custom object";
  };

  const handleAdd = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const optionsStr = String(data.get("options") ?? "").trim();
    const t = target();
    const payload: CustomFieldDefinitionCreate = {
      entity_type: t.kind === "entity" ? t.entity_type : null,
      custom_object_definition_id: t.kind === "object" ? t.cod_id : null,
      key: String(data.get("key") ?? "").trim(),
      label: String(data.get("label") ?? "").trim(),
      field_type: addType(),
      required: data.get("required") === "on",
      help_text: String(data.get("help_text") ?? "").trim() || null,
    };
    if (addType() === "select" || addType() === "multi_select") {
      payload.options = optionsStr
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (payload.options.length === 0) {
        setError("Select / multi-select fields need at least one option (comma-separated).");
        return;
      }
    }
    if (addType() === "lookup") {
      payload.lookup_entity_type = addLookupTarget();
      if (addLookupTarget() === "custom_record") {
        if (!addLookupCodId()) {
          setError("Pick which custom object the lookup should reference.");
          return;
        }
        payload.lookup_custom_object_definition_id = addLookupCodId();
      }
    }
    if (!payload.key || !payload.label) return;
    try {
      await createMutation.mutateAsync(payload);
      setShowAdd(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between flex-wrap gap-3">
          <h3 class="card-title text-base">Custom fields</h3>
          <div class="flex items-center gap-2">
            <label for="cf-entity-type" class="text-sm opacity-70">
              For:
            </label>
            <select
              id="cf-entity-type"
              class="select select-bordered select-sm"
              value={
                target().kind === "entity"
                  ? `entity:${(target() as Extract<Target, { kind: "entity" }>).entity_type}`
                  : `object:${(target() as Extract<Target, { kind: "object" }>).cod_id}`
              }
              onChange={(e) => {
                const v = e.currentTarget.value;
                if (v.startsWith("entity:")) {
                  setTarget({
                    kind: "entity",
                    entity_type: v.slice("entity:".length) as CustomFieldEntityType,
                  });
                } else if (v.startsWith("object:")) {
                  setTarget({ kind: "object", cod_id: v.slice("object:".length) });
                }
              }}
            >
              <For each={ENTITY_TARGETS}>
                {(et) => <option value={`entity:${et}`}>{ENTITY_LABEL[et]}</option>}
              </For>
              <Show when={(customObjects.data?.items.length ?? 0) > 0}>
                <optgroup label="Custom objects">
                  <For each={customObjects.data?.items.filter((o) => !o.archived_at) ?? []}>
                    {(co) => <option value={`object:${co.id}`}>{co.label_singular}</option>}
                  </For>
                </optgroup>
              </Show>
            </select>
          </div>
        </div>

        <Show when={entityFields.isLoading || objectFields.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-6 opacity-60">
            Loading…
          </div>
        </Show>

        <div class="overflow-x-auto mt-2">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Order</th>
                <th>Label</th>
                <th>Key</th>
                <th>Type</th>
                <th>Required</th>
                <th>Options</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <For each={activeFields()}>
                {(f) => (
                  <tr>
                    <td class="text-xs opacity-60">{f.order_index}</td>
                    <td class="font-medium">{f.label}</td>
                    <td class="text-xs opacity-60">
                      <code>{f.key}</code>
                    </td>
                    <td>
                      <span class="badge badge-outline badge-sm">{TYPE_LABEL[f.field_type]}</span>
                    </td>
                    <td>
                      <Show when={f.required}>
                        <span class="badge badge-warning badge-sm">required</span>
                      </Show>
                    </td>
                    <td class="text-xs">
                      <Show when={f.field_type === "lookup" ? f.lookup_entity_type : null}>
                        {(target) => <code>→ {LOOKUP_TARGET_LABEL[target()]}</code>}
                      </Show>
                      <Show when={f.options}>
                        <code>{f.options?.join(", ")}</code>
                      </Show>
                    </td>
                    <td>
                      <button
                        class="btn btn-ghost btn-xs text-error"
                        aria-label={`Archive ${f.label}`}
                        onClick={() =>
                          archiveMutation.mutate({
                            cfdId: f.id,
                            version: f.version,
                          })
                        }
                      >
                        Archive
                      </button>
                    </td>
                  </tr>
                )}
              </For>
              <Show
                when={
                  activeFields().length === 0 && !entityFields.isLoading && !objectFields.isLoading
                }
              >
                <tr>
                  <td colspan={7} class="text-center py-4 opacity-60 text-sm">
                    No custom fields on {targetLabel()}s yet.
                  </td>
                </tr>
              </Show>
            </tbody>
          </table>
        </div>

        <Show when={error()}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            {error()}
          </div>
        </Show>

        <div class="flex justify-end mt-3">
          <button class="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
            Add custom field
          </button>
        </div>

        <Show when={showAdd()}>
          <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="add-cf-title">
            <div class="modal-box">
              <h3 id="add-cf-title" class="font-bold text-lg mb-4">
                Add custom field on {targetLabel()}
              </h3>
              <form onSubmit={handleAdd} class="flex flex-col gap-3">
                <input
                  name="label"
                  class="input input-bordered"
                  placeholder="Display label (e.g. Customer tier)"
                  aria-label="Label"
                  required
                />
                <input
                  name="key"
                  class="input input-bordered"
                  placeholder="key (lowercase, e.g. customer_tier)"
                  aria-label="Field key"
                  pattern="[a-z0-9][a-z0-9_]*"
                  required
                />
                <select
                  name="field_type"
                  class="select select-bordered"
                  aria-label="Field type"
                  value={addType()}
                  onChange={(e) => setAddType(e.currentTarget.value as CustomFieldType)}
                >
                  <For each={Object.keys(TYPE_LABEL) as CustomFieldType[]}>
                    {(t) => <option value={t}>{TYPE_LABEL[t]}</option>}
                  </For>
                </select>
                <Show when={addType() === "select" || addType() === "multi_select"}>
                  <input
                    name="options"
                    class="input input-bordered"
                    placeholder="Comma-separated options (e.g. bronze,silver,gold)"
                    aria-label="Options"
                    required
                  />
                </Show>
                <Show when={addType() === "lookup"}>
                  <select
                    class="select select-bordered"
                    aria-label="Lookup target"
                    value={addLookupTarget()}
                    onChange={(e) =>
                      setAddLookupTarget(e.currentTarget.value as CustomFieldLookupTarget)
                    }
                  >
                    <For each={Object.keys(LOOKUP_TARGET_LABEL) as CustomFieldLookupTarget[]}>
                      {(t) => <option value={t}>References a {LOOKUP_TARGET_LABEL[t]}</option>}
                    </For>
                  </select>
                  <Show when={addLookupTarget() === "custom_record"}>
                    <select
                      class="select select-bordered"
                      aria-label="Which custom object"
                      value={addLookupCodId()}
                      onChange={(e) => setAddLookupCodId(e.currentTarget.value)}
                      required
                    >
                      <option value="">— pick a custom object —</option>
                      <For each={customObjects.data?.items.filter((o) => !o.archived_at) ?? []}>
                        {(co) => <option value={co.id}>{co.label_singular}</option>}
                      </For>
                    </select>
                  </Show>
                </Show>
                <input
                  name="help_text"
                  class="input input-bordered"
                  placeholder="Help text (optional)"
                  aria-label="Help text"
                />
                <label class="label cursor-pointer justify-start gap-2">
                  <input name="required" type="checkbox" class="checkbox checkbox-sm" />
                  <span class="label-text">Required when creating</span>
                </label>
                <div class="modal-action">
                  <button type="button" class="btn btn-ghost" onClick={() => setShowAdd(false)}>
                    Cancel
                  </button>
                  <button type="submit" class="btn btn-primary" disabled={createMutation.isPending}>
                    {createMutation.isPending ? "Saving…" : "Add"}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        </Show>
      </div>
    </div>
  );
}
